// src/App.tsx
import React, { useEffect, useState } from 'react';
import { Routes, Route, Navigate, Outlet, useLocation } from 'react-router-dom';
import { Snackbar, Alert, Box, Typography } from '@mui/material';

import Sidebar, { EXPANDED_WIDTH, MINI_WIDTH } from './components/Sidebar';
import DashboardPage from './pages/Dashboard';
import Login from './pages/Login';
import Home from './pages/Home';
import PrivacyPolicy from './pages/privacy-policy/page';
import TermsAndConditions from './pages/terms-and-conditions/page';
import PublicLayout from './PublicLayout';

// Admin Pages
import ChaptersPage from './pages/Chapters';
import ChapterResources from './pages/ChapterResources';
import DoubtsPage from './pages/Doubts';
import UsersPage from './pages/Users';
import UserDetailsPage from './pages/UserDetails';
import NotificationsPage from './pages/Notifications';
import SubjectsPage from './pages/Subjects';
import ClassesPage from './pages/Classes';
import CreateLongQuestionPage from './pages/resources/CreateLongQuestionPage';
import BundlePricingPage from './pages/BundlePricing';

import { requestForToken, onMessageListener } from './firebase';
import { getAuth } from './utils/authStorage';

const PRIMARY = '#174143';

interface AppNotification {
    title: string;
    body: string;
}

const PAGE_TITLES: Record<string, string> = {
    '/dashboard':     'Dashboard',
    '/subjects':      'Subjects',
    '/classes':       'Classes',
    '/chapters':      'Chapters',
    '/doubts':        'Doubts',
    '/users':         'Users',
    '/notifications': 'Notifications',
    '/settings':      'Settings',
    '/bundle-pricing': 'Bundle Pricing',
};

function getPageTitle(pathname: string): string {
    for (const [path, title] of Object.entries(PAGE_TITLES)) {
        if (pathname === path || pathname.startsWith(path + '/')) return title;
    }
    return 'Vidya Samruddhi';
}

function AppLayout(): React.ReactElement {
    const [notification, setNotification] = useState<AppNotification | null>(null);
    const [collapsed, setCollapsed] = useState(false);
    const auth = getAuth();
    const location = useLocation();
    const pageTitle = getPageTitle(location.pathname);
    const sidebarWidth = collapsed ? MINI_WIDTH : EXPANDED_WIDTH;

    useEffect(() => {
        if (!auth || !auth.accessToken) return;
        requestForToken();
        onMessageListener()
            .then((payload) => {
                setNotification({
                    title: payload.notification?.title ?? 'New Notification',
                    body: payload.notification?.body ?? '',
                });
            })
            .catch((err) => console.warn('FCM onMessageListener error:', err));
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    if (!auth || !auth.accessToken) {
        return <Navigate to="/login" replace />;
    }

    return (
        <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: '#f8fafb' }}>
            <Sidebar collapsed={collapsed} onToggle={() => setCollapsed((c) => !c)} />

            {/* Main area — shifts right to account for sidebar */}
            <Box
                component="div"
                sx={{
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    ml: { xs: 0, md: `${sidebarWidth}px` },
                    transition: 'margin-left 0.25s ease',
                    minWidth: 0,
                }}
            >
                {/* Top header bar */}
                <Box
                    component="header"
                    sx={{
                        position: 'sticky',
                        top: 0,
                        zIndex: (t) => t.zIndex.appBar,
                        height: 64,
                        display: 'flex',
                        alignItems: 'center',
                        px: { xs: 6, md: 3 },
                        bgcolor: '#ffffff',
                        borderBottom: '1px solid #e5e7eb',
                        boxShadow: '0 1px 4px rgba(0,0,0,0.04)',
                        gap: 2,
                    }}
                >
                    {/* Mobile menu button (shown via Sidebar internally, this is a spacer) */}
                    <Box sx={{ display: { xs: 'block', md: 'none' }, width: 40 }} />

                    <Typography
                        variant="h6"
                        fontWeight={700}
                        sx={{ color: PRIMARY, fontSize: '1.1rem' }}
                    >
                        {pageTitle}
                    </Typography>
                </Box>

                {/* Page content */}
                <Box
                    component="main"
                    sx={{
                        flex: 1,
                        p: { xs: 2, sm: 3 },
                        maxWidth: '100%',
                        overflow: 'auto',
                    }}
                >
                    <Outlet />
                </Box>
            </Box>

            <Snackbar
                open={!!notification}
                autoHideDuration={6000}
                onClose={() => setNotification(null)}
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
                <Alert onClose={() => setNotification(null)} severity="info" sx={{ width: '100%' }}>
                    <strong>{notification?.title}</strong>
                    <br />
                    {notification?.body}
                </Alert>
            </Snackbar>
        </Box>
    );
}

export default function App(): React.ReactElement {
    return (
        <Routes>
            {/* Public routes */}
            <Route element={<PublicLayout />}>
                <Route path="/" element={<Home />} />
                <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                <Route path="/terms-and-conditions" element={<TermsAndConditions />} />
            </Route>

            {/* Login */}
            <Route path="/login" element={<Login />} />

            {/* Protected admin routes */}
            <Route element={<AppLayout />}>
                <Route path="dashboard" element={<DashboardPage />} />
                <Route path="subjects" element={<SubjectsPage />} />
                <Route path="classes" element={<ClassesPage />} />
                <Route path="chapters" element={<ChaptersPage />} />
                <Route path="chapters/resources/:id" element={<ChapterResources />} />
                <Route path="chapters/resources/:id/question/:marks" element={<CreateLongQuestionPage />} />
                <Route path="doubts" element={<DoubtsPage />} />
                <Route path="users" element={<UsersPage />} />
                <Route path="users/:id" element={<UserDetailsPage />} />
                <Route path="notifications" element={<NotificationsPage />} />
                <Route path="bundle-pricing" element={<BundlePricingPage />} />
                <Route path="settings" element={<div>Settings (TODO)</div>} />
            </Route>

            <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
    );
}
